import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChoiceGrid, MultiChoiceGrid, StepFooter, StepHeader } from "./_widgets";

const TRACK = [
  { value: "daily", label: "Yes, daily" },
  { value: "sometimes", label: "Sometimes" },
  { value: "no", label: "No" },
];

const STRUGGLES = [
  { value: "portion_control", label: "Portion control" },
  { value: "cravings", label: "Cravings" },
  { value: "late_night", label: "Late-night eating" },
  { value: "alcohol", label: "Alcohol" },
  { value: "eating_out", label: "Eating out" },
  { value: "weekend", label: "Weekend overeating" },
  { value: "consistency", label: "Consistency" },
];

const DIETARY = [
  { value: "none", label: "None" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten_free", label: "Gluten-Free" },
  { value: "dairy_free", label: "Dairy-Free" },
  { value: "other", label: "Other" },
];

const WATER = [
  { value: "under_1l", label: "Under 1 L" },
  { value: "1_2l", label: "1 – 2 L" },
  { value: "2_3l", label: "2 – 3 L" },
  { value: "over_3l", label: "3 L +" },
];

export default function Step5Nutrition({ data, set, onNext }) {
  const valid =
    !!data.food_tracking &&
    data.daily_calories !== "" &&
    data.daily_protein !== "" &&
    data.daily_carbs !== "" &&
    data.daily_fats !== "" &&
    (data.nutrition_struggles || []).length > 0 &&
    (data.dietary_restrictions || []).length > 0 &&
    !!data.water_intake;

  return (
    <div className="space-y-5">
      <StepHeader title="Nutrition" subtitle="So we can dial in your calories & macros." />
      <HealthSourceNote topic="food" context="These survey fields are your reported intake, not calculated targets." />

      <div className="space-y-1.5">
        <Label>Do you currently track your food?</Label>
        <ChoiceGrid
          options={TRACK}
          value={data.food_tracking}
          onChange={(v) => set({ food_tracking: v })}
          testid="onb-food-tracking"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label>Calories / day</Label>
          <Input
            type="number"
            placeholder="e.g. 2200"
            value={data.daily_calories}
            data-testid="onb-calories"
            onChange={(e) => set({ daily_calories: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Protein (g)</Label>
          <Input
            type="number"
            placeholder="e.g. 180"
            value={data.daily_protein}
            data-testid="onb-protein"
            onChange={(e) => set({ daily_protein: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Carbs (g)</Label>
          <Input
            type="number"
            placeholder="e.g. 220"
            value={data.daily_carbs}
            data-testid="onb-carbs"
            onChange={(e) => set({ daily_carbs: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Fats (g)</Label>
          <Input
            type="number"
            placeholder="e.g. 70"
            value={data.daily_fats}
            data-testid="onb-fats"
            onChange={(e) => set({ daily_fats: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Biggest nutrition struggles</Label>
        <MultiChoiceGrid
          options={STRUGGLES}
          value={data.nutrition_struggles || []}
          onChange={(v) => set({ nutrition_struggles: v })}
          testid="onb-struggles"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Food allergies / restrictions</Label>
        <MultiChoiceGrid
          options={DIETARY}
          value={data.dietary_restrictions || []}
          onChange={(v) => set({ dietary_restrictions: v })}
          testid="onb-dietary"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Water per day</Label>
        <ChoiceGrid
          options={WATER}
          value={data.water_intake}
          onChange={(v) => set({ water_intake: v })}
          columns={2}
          testid="onb-water"
        />
      </div>

      <StepFooter valid={valid} onNext={onNext} />
    </div>
  );
}
